import type { SupportedDisplayLanguage } from '@/displayLanguage/displayLanguageService'
import { hostTerritory, ROE_TERRITORIES, ROW_NA_TERRITORIES, weblabMarketplace } from '@/territory'

// Wire LocaleCodes the app ships UI translations for. The picker offers only
// the subset assigned to the customer's music territory — not every locale —
// matching hornet-app's per-territory TerritoryLocales allow-list. The first
// entry is the marketplace's own language.
const DEFAULT_TERRITORY_LANGUAGES = [
    'en_US',
    'es_US',
    'fr_CA',
    'de_DE',
    'it_IT',
    'ja_JP',
    'pt_BR',
] as const satisfies readonly SupportedDisplayLanguage[]

const TERRITORY_LANGUAGES: Record<string, readonly SupportedDisplayLanguage[] | undefined> = {
    US: DEFAULT_TERRITORY_LANGUAGES,
    CA: ['en_CA', 'fr_CA'],
    MX: ['es_MX', 'en_US'],
    BR: ['pt_BR', 'en_US'],
    GB: ['en_GB', 'es_ES', 'fr_FR', 'de_DE', 'it_IT'],
    DE: ['de_DE', 'en_GB', 'es_ES', 'fr_FR', 'it_IT'],
    FR: ['fr_FR', 'en_GB', 'es_ES', 'de_DE', 'it_IT'],
    IT: ['it_IT', 'en_GB', 'es_ES', 'fr_FR', 'de_DE'],
    ES: ['es_ES', 'en_GB', 'fr_FR', 'de_DE', 'it_IT'],
    IN: ['en_IN'],
    JP: ['ja_JP', 'en_US'],
    AU: ['en_AU'],
    NZ: ['en_AU'],
    ...Object.fromEntries(
        [...ROW_NA_TERRITORIES].map((territory) => [territory, ['en_US', 'es_US']])
    ),
    ...Object.fromEntries([...ROE_TERRITORIES].map((territory) => [territory, ['en_GB']])),
}

/** The display languages offered in a music territory, the default offer for an unknown one. */
export function displayLanguagesFor(territory: string): readonly SupportedDisplayLanguage[] {
    return TERRITORY_LANGUAGES[territory] ?? DEFAULT_TERRITORY_LANGUAGES
}

/** The default offer when the customer's territory is not resolved yet. */
export function defaultDisplayLanguages(): readonly SupportedDisplayLanguage[] {
    return DEFAULT_TERRITORY_LANGUAGES
}

/** The page host's marketplace language: what a signed-out visitor with no saved choice sees. */
export function hostMarketplaceLanguage(): SupportedDisplayLanguage {
    return displayLanguagesFor(weblabMarketplace(hostTerritory()))[0]
}
